import { inject, Injectable } from '@angular/core';
import { ActionItem, ActionItemStyle, Mh2Entity, NavigationLink } from '@stylehaven/components';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserAction } from '../common/entities/common/user-action.entity';
import { BulkUserAction } from '../common/entities/common/bulk-user-action.entity';
import { BatchUserAction } from '../common/entities/common/batch-user-action.entity';
import { GenericActionService } from './generic-action.service.interface';
import { ActionTooltipHelper } from '../common/helpers/actions/action-tooltip.helper';
import { ActionItemOptions } from '../common/entities/common/action-item-function.entity';

@Injectable({ providedIn: 'root' })
export abstract class GenericActionLinkService<
    T extends object,
    S extends string,
    U extends UserAction | BulkUserAction | BatchUserAction = UserAction
> extends GenericActionService<T, S, U> {
    protected readonly tooltipHelper: ActionTooltipHelper = inject(ActionTooltipHelper);

    // #region Action
    public getActionItem = (
        target: T,
        action: S,
        actionItemOptions: ActionItemOptions
    ): Observable<ActionItem | null> => {
        return combineLatest([
            this.getUserAction(target, actionItemOptions),
            this.getLink(target, actionItemOptions)
        ]).pipe(
            map(([userAction, link]) => {
                if (!userAction.isAvailable()) {
                    return null;
                }

                const icon: string | undefined = this.getIcon(target, actionItemOptions);
                const style: ActionItemStyle = icon ? ActionItemStyle.PureIconButton : this.BUTTON_STYLE;

                return {
                    label: this.translateService.translate(this.getLabelKey(target)),
                    navigationLink: link,
                    disabled: userAction.isDisabled(),
                    disabledInfoMsg: this.getDisabledMessage(target, userAction),
                    icon,
                    style
                };
            })
        );
    };

    protected abstract getDisabledMessage(target: T, userAction: U): string;

    /**
     * Action implementation
     * Should return an observable boolean if the action was successful or not
     *
     * @param record
     * @param actionItemOptions
     */
    protected abstract getLink(target: T, actionItemOptions: ActionItemOptions): Observable<NavigationLink>;
    // #endregion

    // #region On Action => Not applicable
    protected onAction(target: T, actionItemOptions: ActionItemOptions): Observable<boolean> {
        return of(true);
    }

    // #endregion

    // #region Toast Config => Not applicable
    public getToastLabelKey(target: T): string {
        return '';
    }

    public getToastEntity(target: T, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return '';
    }

    public getSuccessToastLinkLabelKey(target: T, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public getSuccessToastLink(target: T, actionItemOptions: ActionItemOptions): NavigationLink | undefined {
        return undefined;
    }

    public getToastTranslationOptions(target: T, actionItemOptions: ActionItemOptions): Record<string, string> {
        return {};
    }
    // #endregion

    // #region Executing => Not applicable
    public getActionExecuting(target: T): Observable<boolean> {
        return of(false);
    }

    public setActionExecutionStart(target: T): void {
        return;
    }

    public setActionExecutionEnd(target: T): void {
        return;
    }
    // #endregion
}
